import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useDeleteNomination, useEvent, useUpdateNomination } from '@/api/queries';
import { Button } from '@/components/kit/Button';
import { ChoiceGroup, Switch, TextLink } from '@/components/kit/Controls';
import { Col, Form, NumberField, Row, TextAreaField, TextField } from '@/components/kit/Field';
import { Breadcrumbs, Columns, Kv, PageHeader } from '@/components/kit/Layout';
import { MetricRow } from '@/components/kit/MetricRow';
import { Alert, EmptyState, ErrorState, Note, TableSkeleton } from '@/components/kit/Note';
import { Panel, Signals, Stack } from '@/components/kit/Panel';
import { Pill } from '@/components/kit/Pill';
import { CardLabel, MobileSub, Table } from '@/components/kit/Table';
import { useToast } from '@/components/kit/Toast';
import { ageRange, date as fmtDate, mmss, money, num } from '@/lib/format';

/**
 * Карточка номинации.
 *
 * Перечень доступных пакетов здесь **вычисляется и только читается**:
 * список номинаций живёт на пакете, иначе один пакет правился бы
 * в шести местах (БЛ §5.2).
 *
 * Блок и порядок в программе тоже только показаны: их задают
 * на вкладке «Программа», где видна вся программа дня целиком.
 */
export function NominationPage() {
  const { eventId, nominationId } = useParams<{ eventId: string; nominationId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const query = useEvent(eventId);
  const update = useUpdateNomination(eventId!);
  const remove = useDeleteNomination(eventId!);

  if (query.isError) {
    return (
      <div className="main">
        <Panel>
          <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
        </Panel>
      </div>
    );
  }

  if (query.isLoading || !query.data) {
    return (
      <div className="main">
        <PageHeader title="Номинация" />
        <Panel flush>
          <TableSkeleton rows={6} cols={4} />
        </Panel>
      </div>
    );
  }

  const event = query.data;
  const nomination = event.nominations.find((n) => n.id === nominationId);

  if (!nomination) {
    return (
      <div className="main">
        <Panel>
          <EmptyState>Номинация не найдена — возможно, её удалили</EmptyState>
        </Panel>
      </div>
    );
  }

  const n = nomination;
  const block = event.blocks.find((b) => b.id === n.blockId);
  const orderInBlock = block
    ? block.items.findIndex((i) => i.nominationId === n.id) + 1
    : 0;

  const available = event.packages.filter((p) =>
    p.nominations.some((link) => link.nominationId === n.id),
  );
  const badDefault = event.computed.nominationsWithBadDefault.includes(n.id);

  const fail = (e: Error) => toast.showError(e instanceof ApiError ? e.message : 'Не сохранилось');
  const save = (patch: Record<string, unknown>) =>
    update.mutate({ id: n.id, ...patch }, { onSuccess: () => toast.show('Сохранено'), onError: fail });

  /** Числовые поля — по окончании ввода; промис, чтобы поле откатилось при ошибке */
  const commit = (patch: Record<string, unknown>) =>
    update.mutateAsync({ id: n.id, ...patch }).then(
      () => toast.show('Сохранено'),
      (e: Error) => {
        fail(e);
        throw e;
      },
    );

  const goTab = (tab: string) => navigate(`/events/${event.id}/${tab}`);

  return (
    <div className="main">
      <Breadcrumbs
        items={[
          { label: 'Мероприятия', onClick: () => navigate('/events') },
          { label: event.city, onClick: () => goTab('nominations') },
          { label: n.name },
        ]}
      />

      <PageHeader
        title={n.name}
        before={
          <Pill tone={n.isCompetitive ? 'ok' : 'info'}>
            {n.isCompetitive ? 'Конкурсная' : 'Показательная'}
          </Pill>
        }
      >
        <Button
          tone="danger"
          onClick={() =>
            remove.mutate(n.id, {
              onSuccess: () => {
                toast.show(`«${n.name}» удалена`);
                goTab('nominations');
              },
              onError: fail,
            })
          }
        >
          Удалить
        </Button>
      </PageHeader>

      <Signals>
        {badDefault ? (
          <Alert
            action={
              available.length ? (
                <Button
                  small
                  tone="quiet"
                  onClick={() => navigate(`/events/${event.id}/packages/${available[0].id}`)}
                >
                  Открыть пакет
                </Button>
              ) : undefined
            }
          >
            <b>
              {n.defaultPackageId ? 'Пакетов по умолчанию несколько' : 'Пакет по умолчанию не задан'}.
            </b>
            <span>Клиент не поймёт, что покупать при регистрации в эту номинацию</span>
          </Alert>
        ) : null}
      </Signals>

      {/* Паспорт номинации — ряд цифр обзора, без героев (кит 2.11) */}
      <MetricRow
        variant="sum"
        items={[
          { label: 'Лимит команд', value: num(n.teamLimit), sub: 'дальше резерв' },
          { label: 'Возраст', value: ageRange(n.ageFrom, n.ageTo), sub: 'включительно' },
          {
            label: 'Хронометраж',
            value: mmss(n.durationLimitSec),
            sub: 'штраф 0,1 балла за секунду сверх',
          },
          {
            label: 'Плановое время',
            value: mmss(n.teamLimit * n.avgDurationSec),
            sub: 'лимит × среднее время номера',
          },
        ]}
      />

      <Columns>
        <Stack>
          <Panel title="Правила номинации">
            <Form>
              <TextField
                label="Название"
                key={`name-${n.name}`}
                defaultValue={n.name}
                onBlur={(e) => {
                  const next = e.target.value.trim();
                  if (next && next !== n.name) save({ name: next });
                }}
              />

              <Row>
                <Col fix>
                  <NumberField
                    label="Возраст от"
                    placeholder="без границы"
                    size={12}
                    value={n.ageFrom}
                    onCommit={(v) => commit({ ageFrom: v })}
                  />
                </Col>
                <Col fix>
                  <NumberField
                    label="Возраст до"
                    placeholder="без границы"
                    size={12}
                    value={n.ageTo}
                    onCommit={(v) => commit({ ageTo: v })}
                  />
                </Col>
              </Row>

              <Note>
                Границы включительно. По этим числам система сверяет даты рождения и считает,
                сколько человек вне возраста — поэтому возраст хранится числами, а не строкой
              </Note>

              <Row>
                <Col>
                  <NumberField
                    label="Лимит команд"
                    value={n.teamLimit}
                    onCommit={(v) => ((v ?? 0) !== n.teamLimit ? commit({ teamLimit: v ?? 0 }) : undefined)}
                  />
                </Col>
                <Col>
                  <NumberField
                    label="Процент из другого возраста"
                    placeholder="не допускается"
                    value={n.otherAgePct}
                    onCommit={(v) => commit({ otherAgePct: v })}
                  />
                </Col>
              </Row>

              <Row>
                <Col>
                  <NumberField
                    label="Лимит хронометража, сек"
                    value={n.durationLimitSec}
                    onCommit={(v) => (v ? commit({ durationLimitSec: v }) : undefined)}
                  />
                </Col>
                <Col>
                  <NumberField
                    label="Среднее время номера, сек"
                    value={n.avgDurationSec}
                    onCommit={(v) => (v ? commit({ avgDurationSec: v }) : undefined)}
                  />
                </Col>
              </Row>

              <NumberField
                label="Минимальный состав"
                value={n.minMembers}
                onCommit={(v) => commit({ minMembers: v })}
              />

              <TextAreaField
                label="Описание"
                rows={4}
                key={`desc-${n.description}`}
                defaultValue={n.description ?? ''}
                onBlur={(e) => {
                  const next = e.target.value.trim();
                  if (next !== (n.description ?? '')) save({ description: next || null });
                }}
              />
            </Form>
            <Note>
              Лимит: когда столько команд оплатят участие, новые регистрации пойдут в резерв.
              Среднее время номера нужно, чтобы прикинуть длину блока, пока музыка не загружена.
              Описание видит руководитель при выборе номинации
            </Note>
          </Panel>

          <Panel title="Определение мест">
            <ChoiceGroup
              value={n.isCompetitive ? 'competitive' : 'showcase'}
              onChange={(v) => save({ isCompetitive: v === 'competitive' })}
              items={[
                { value: 'competitive', label: 'Конкурсная' },
                { value: 'showcase', label: 'Показательная' },
              ]}
            />
            {!n.isCompetitive ? (
              <Note>
                Мест нет. Все команды получают лауреатов 1, 2 и 3 степени, степень распределяется
                равномерно: на 10 команд 4/3/3
              </Note>
            ) : null}
          </Panel>

          <Panel title="Руководитель в составе">
            <Switch
              checked={n.leaderOnStage}
              onToggle={() => save({ leaderOnStage: !n.leaderOnStage })}
              label="Может выходить на сцену"
            />
            {n.leaderOnStage ? (
              <Note>
                Выход оплачивается отдельной позицией. На классы руководитель ходит бесплатно
                в любом случае
              </Note>
            ) : null}
          </Panel>

          {/* M3: как номинация участвует в регистрациях (БЛ M3 5.13, 5.21) */}
          <Panel title="Регистрация">
            <Switch
              checked={n.isClosed}
              onToggle={() => save({ isClosed: !n.isClosed })}
              label="Номинация закрыта — новые регистрации встают в резерв"
            />
            <Note>
              Лимит команд справочный: по нему считаются план и заполненность, при превышении — предупреждение.
              Регистрацию он не останавливает — это делает только этот переключатель. Уже созданные номера закрытие не
              трогает; поднять из резерва менеджер может всегда.
            </Note>
            <Switch
              checked={n.isSolo}
              onToggle={() => save({ isSolo: !n.isSolo })}
              label="Сольная — вместо команды выбирают солиста"
            />
            <Switch checked={n.needVideo} onToggle={() => save({ needVideo: !n.needVideo })} label="Нужно видео" />
            <Switch
              checked={n.needCostume}
              onToggle={() => save({ needCostume: !n.needCostume })}
              label="Нужны фото костюмов"
            />
            <Note>
              Видео и костюмы — пункты чек-листа номера по умолчанию; в конкретном номере их можно
              переопределить
            </Note>
          </Panel>

          <Panel title="Доступные пакеты" flush={available.length > 0}>
            {available.length ? (
              <Table
                valueWidth={130}
                head={
                  <tr>
                    <th>Пакет</th>
                    <th>Роль</th>
                    <th className="r">Услуг</th>
                    <th className="r">Цена</th>
                    <th />
                  </tr>
                }
              >
                {available.map((p) => {
                  const isDefault = p.nominations.some(
                    (l) => l.nominationId === n.id && l.isDefault,
                  );
                  return (
                    <tr
                      key={p.id}
                      className="clk"
                      onClick={() => navigate(`/events/${event.id}/packages/${p.id}`)}
                    >
                      <td data-col="primary">
                        <span className="nm">{p.name}</span>
                        <MobileSub>
                          {isDefault ? 'по умолчанию' : 'ставит менеджер'} · {money(p.priceFirst)}
                        </MobileSub>
                      </td>
                      <td data-col="secondary">
                        <Pill tone={isDefault ? 'ok' : 'mut'}>
                          {isDefault ? 'по умолчанию' : 'ставит менеджер'}
                        </Pill>
                      </td>
                      <td className="r num muted" data-col="hide">
                        {p.serviceIds.length || <span className="na">—</span>}
                      </td>
                      <td className="r" data-col="sec2">
                        <CardLabel>цена</CardLabel>
                        <span className="num">{money(p.priceFirst)}</span>
                      </td>
                      <td className="r" data-col="act" />
                    </tr>
                  );
                })}
              </Table>
            ) : (
              <EmptyState>
                Ни один пакет не включает эту номинацию.
                <br />
                <TextLink onClick={() => goTab('packages')}>Открыть пакеты</TextLink>
              </EmptyState>
            )}
          </Panel>
        </Stack>

        <Panel title="Программа">
          <Kv k="Блок">{block ? block.name : <span className="wtx">вне программы</span>}</Kv>
          <Kv k="Дата">
            <span className="num">{block ? fmtDate(block.date) : '—'}</span>
          </Kv>
          <Kv k="Порядок в блоке">
            <span className="num">{block ? `№ ${orderInBlock} из ${block.items.length}` : '—'}</span>
          </Kv>
          <Note>
            Блок и порядок задаются на вкладке «Программа»: там видна вся программа дня целиком,
            а не по одной номинации. <TextLink onClick={() => goTab('program')}>Открыть программу</TextLink>
          </Note>
        </Panel>
      </Columns>
    </div>
  );
}
